/*
  useStickyState — a tiny drop-in replacement for `useState` that
  transparently persists to localStorage under a caller-supplied
  key. Handy for operator preferences that should survive a
  session refresh (sort order, filter mode) without needing a
  full-blown server-side settings write.

  Usage:
    const [sort, setSort] = useStickyState("status", "docImport.batchesSort");

  * Reads the persisted value on mount (falls back to `initial`).
  * Writes on every set so the value survives a reload.
  * Safe against JSON-parse errors + a missing `window` (SSR).
*/
import { useEffect, useRef, useState } from "react";

export default function useStickyState(initial, key) {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined" || !key) return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return initial;
      return JSON.parse(raw);
    } catch {
      return initial;
    }
  });
  // Skip the write on the very first render — the initial value
  // already came from storage (or the default), so writing it back
  // is redundant and would blow away a value that a concurrent tab
  // just changed.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (typeof window === "undefined" || !key) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch { /* quota / SecurityError — ignore silently */ }
  }, [key, value]);
  return [value, setValue];
}
